import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, ListChecks } from 'lucide-react';
import type { OrderStateToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useOrderStatesList, useOrderStateMutations } from '~/hooks/use-order-states';
import { tokenToForm, type OrderStateFormValues } from '~/lib/schemas/order-state';
import { OrderStateEditDialog } from './order-state-edit-dialog';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Internal order statuses (Εσωτερικές Καταστάσεις) CRUD surface — parity with the legacy admin grid (Code/Name/ERP code/Visible/Sort). */
export function OrderStatesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useOrderStatesList();
	const { remove } = useOrderStateMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<OrderStateFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<OrderStateToken | null>(null);

	const columns = useMemo<SystemGridColumn<OrderStateToken>[]>(
		() => [
			{
				key: 'name',
				header: t('orders.orderStates.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.nm ?? '',
				exportValue: o => o.nm ?? '',
				render: o => (
					<span className="truncate text-sm font-medium text-foreground" title={o.nm ?? ''}>
						{o.nm || '—'}
					</span>
				)
			},
			{
				key: 'code',
				header: t('orders.orderStates.columns.code'),
				width: '140px',
				sortValue: o => o.cd ?? '',
				exportValue: o => o.cd ?? '',
				render: o => <span className="truncate font-mono text-xs text-foreground-muted">{o.cd || '—'}</span>
			},
			{
				key: 'erpCode',
				header: t('orders.orderStates.columns.erpCode'),
				width: '140px',
				sortValue: o => o.erpcd ?? '',
				exportValue: o => o.erpcd ?? '',
				render: o => <span className="truncate font-mono text-xs text-foreground-muted">{o.erpcd || '—'}</span>
			},
			{
				key: 'visible',
				header: t('orders.orderStates.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: o => (o.iv ? 1 : 0),
				exportValue: o => (o.iv ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.iv)} />
			},
			{
				key: 'sort',
				header: t('orders.orderStates.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: o => o.si ?? 0,
				exportValue: o => o.si ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{o.si}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<OrderStateToken>
				gridId="order-states"
				title={t('orders.orderStates.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.nm}
				columns={columns}
				searchPredicate={(o, q) =>
					(o.nm ?? '').toLowerCase().includes(q) || (o.cd ?? '').toLowerCase().includes(q) || (o.erpcd ?? '').toLowerCase().includes(q)
				}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<ListChecks size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('orders.orderStates.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditing(tokenToForm(o));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="order-states"
			/>

			<OrderStateEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('orders.orderStates.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('orders.orderStates.deleteBody', { name: deleteRow?.nm || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
